import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '../../../../components/ui/DataTable';
import { Download, Eye, Store, Plus } from 'lucide-react';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../components/ui/SearchInput';
import { Button } from '../../../../components/ui/Button';
import { fetchSellers, requestDownloadSellers } from '../../../../features/sellers/sellersThunks';
import { setSearchFilter, setPage, setRowsPerPage } from '../../../../features/sellers/sellersSlice';
import { selectIsKindRunning } from '../../../../features/notifications/notificationsSlice';
import { NOTIFICATION_KIND } from '../../../../features/notifications/notificationsConstants';
import { selectAdminLabelById } from '../../../../features/admins/adminsSlice';
import { fetchAdminOptions } from '../../../../features/admins/adminsThunks';

export const Sellers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sellers, totalCount, loading, listError, filters } = useSelector(state => state.sellers);
  const isDownloading = useSelector(selectIsKindRunning(NOTIFICATION_KIND.DOWNLOAD_SELLERS));
  // This table holds every seller of the platform, so each row has to say whose
  // it is. The listing carries the owner's id and nothing else.
  const adminLabelById = useSelector(selectAdminLabelById);

  useEffect(() => {
    dispatch(fetchAdminOptions());
  }, [dispatch]);

  const [searchInput, setSearchInput] = useState(filters.search);

  // Debounce for search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchInput !== filters.search) {
        dispatch(setSearchFilter(searchInput));
      }
    }, 500); // 500ms debounce
    return () => clearTimeout(handler);
  }, [searchInput, filters.search, dispatch]);

  // Fetch when relevant filters change
  useEffect(() => {
    dispatch(fetchSellers());
  }, [
    dispatch,
    filters.search,
    filters.page,
    filters.rowsPerPage
  ]);

  // No confirmation of its own: the thunk opens an entry in the Centro de
  // Actividad on the way out, and that entry is what reports the outcome when
  // the worker finishes — minutes after this handler is gone.
  const handleDownloadClick = () => {
    dispatch(requestDownloadSellers());
  };

  // Grouped in pairs, the way `/deliveries` composes its cells: nine columns of
  // a single field each added up to more than the screen and left the listing
  // scrolling sideways. Plan and KAM are not here anymore — both are in the
  // seller's detail, which is where they are acted on.
  const columns = [
    {
      header: 'SELLER',
      cell: (row) => (
        <span className="font-bold text-ninja-dark dark:text-white">
          {row.company?.social_reason || '-'}
        </span>
      )
    },
    {
      header: 'ENCARGADO',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-ninja-dark dark:text-white">
            {`${row.user?.first_name || ''} ${row.user?.last_name || ''}`.trim() || '-'}
          </span>
          <span className="text-xs text-text-muted mt-0.5">{row.rut || '-'}</span>
        </div>
      )
    },
    {
      // Counted by the backend: the listing carries the seller and its company,
      // never the collections themselves, so reading a length here showed 0 for
      // everyone.
      header: 'OPERACIÓN',
      cell: (row) => {
        const warehouses = row.warehouses_count ?? 0;
        const operators = row.operators_count ?? 0;
        return (
          <div className="flex flex-col">
            <span data-testid="seller-warehouses-count" className="text-ninja-dark dark:text-white">
              {warehouses} bodega{warehouses === 1 ? '' : 's'}
            </span>
            <span data-testid="seller-operators-count" className="text-xs text-text-muted mt-0.5">
              {operators} operador{operators === 1 ? '' : 'es'}
            </span>
          </div>
        );
      }
    },
    {
      header: 'OPERADOR',
      cell: (row) => {
        const label = adminLabelById[row.admin];
        if (!label) return <span className="text-text-muted">Admin #{row.admin ?? '—'}</span>;

        // The catalog labels an operator as "correo (Nombre Apellido)", which is
        // what a select needs and what would widen this column past the screen.
        // Here the address is enough and the rest lives in the tooltip.
        return (
          <span title={label} className="block max-w-[180px] truncate text-text-secondary">
            {label.split(' (')[0]}
          </span>
        );
      },
    },
    {
      header: 'CREACIÓN', cell: (row) => {
        const dateStr = row.company?.created_at || row.user?.date_joined;
        if (!dateStr) return '-';
        return <span className="text-text-secondary">{new Date(dateStr).toLocaleDateString()}</span>;
      }
    },
    {
      header: 'ACCIONES', cell: (row) => (
        <div className="flex items-center justify-end">
          <button
            onClick={() => navigate(`/sellers/${row.company?.id}`)}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-ninja-green hover:bg-ninja-greenLight transition-colors"
            title="Ver detalle"
          >
            <Eye size={16} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <PageHeader
        icon={Store}
        iconClassName="bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400"
        title="Sellers"
        description="Todos los sellers de la plataforma, con el operador al que pertenecen."
        actions={
          <Button variant="primary" icon={Plus} onClick={() => navigate('/sellers/new')}>
            Crear Seller
          </Button>
        }
      />

      {/* Loose on the page background: inside the table's card it read as one
          box cut in half by a border. */}
      <FilterBar
        variant="bare"
        actions={
          <Button
            variant="ghost"
            icon={Download}
            onClick={handleDownloadClick}
            loading={isDownloading}
            loadingLabel="Generando..."
            title={isDownloading ? 'Ya hay un reporte en curso' : undefined}
          >
            Descargar reporte
          </Button>
        }
      >
        <SearchInput
          variant="bare"
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Buscar sellers por nombre, rut..."
        />
      </FilterBar>

      <DataTable
        columns={columns}
        data={sellers}
        totalItems={totalCount}
        currentPage={filters.page}
        itemsPerPage={filters.rowsPerPage}
        isLoading={loading}
        error={listError}
        onRetry={() => dispatch(fetchSellers())}
        itemLabel="sellers"
        onPageChange={(page) => dispatch(setPage(page))}
        onRowsPerPageChange={(rowsPerPage) => dispatch(setRowsPerPage(rowsPerPage))}
      />
    </div>
  );
};
